import { useId } from "react";
import { PERFECT_MONTH_NAMES, lessonMilestoneLabel, type AwardDefinition } from "@wmf/shared/studentAwards";
import { LessonInstrumentSymbol } from "./LessonInstrumentSymbol.js";

const silhouettes = {
  circle: "M100 7a93 93 0 1 1 0 186 93 93 0 1 1 0-186Z",
  square: "M43 9h114a34 34 0 0 1 34 34v114a34 34 0 0 1-34 34H43a34 34 0 0 1-34-34V43A34 34 0 0 1 43 9Z",
  shield: "M100 6Q144 26 184 31l-5 64c-4 49-34 79-79 101C55 174 25 144 21 95l-5-64Q56 26 100 6Z",
  hexagon: "M100 6 183 52v96l-83 47-83-47V52Z",
  star: "M100 6 132 66 189 79 149 128 155 196 100 152 45 196 51 128 11 79 68 66Z",
};
const lettering = { textAnchor: "middle" as const, fontFamily: "Arial, sans-serif", fontWeight: 700 };

function CalendarSymbol({ checked = false }: { checked?: boolean }) {
  return <g transform="translate(0 -8)">
    <path d="M62 61h76q10 0 10 11v68q0 11-10 11H62q-10 0-10-11V72q0-11 10-11Z" fill="none" stroke="currentColor" strokeWidth="8" strokeLinejoin="round"/>
    <path d="M54 84h92M76 51v20m48-20v20" fill="none" stroke="currentColor" strokeWidth="8" strokeLinecap="round"/>
    {checked ? [74, 100, 126].flatMap(x => [99, 119, 139].map(y =>
      <path key={x + "-" + y} d={"M" + (x - 6) + " " + (y - 1) + "l4 4 8-9"} fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
    )) : [72, 111].flatMap(x => [97, 124].map(y => <rect key={x + "-" + y} x={x} y={y} width="17" height="14" rx="2"/>))}
  </g>;
}

/** The medal rim is the clock rim; the milestone sits behind the hands. */
function ClockFace({ minutes }: { minutes: number }) {
  return <>
    <text {...lettering} x="100" y="126" fontSize="73" opacity=".34">{minutes}</text>
    {Array.from({ length: 60 }, (_, i) => <path key={i} d={i % 5 === 0 ? "M100 25v10" : "M100 25v4"}
      transform={"rotate(" + i * 6 + " 100 100)"} fill="none" stroke="currentColor" strokeWidth={i % 5 === 0 ? 3.7 : 1.5} strokeLinecap="round"/>)}
    <path d="M100 100V62" transform={"rotate(" + minutes / 2 + " 100 100)"} fill="none" stroke="currentColor" strokeWidth="8" strokeLinecap="round"/>
    <path d="M100 100V43" transform={"rotate(" + (minutes % 60) * 6 + " 100 100)"} fill="none" stroke="currentColor" strokeWidth="5.5" strokeLinecap="round"/>
    <circle cx="100" cy="100" r="6"/>
  </>;
}

function Trophy() {
  return <>
    <path d="M76 57h48v37c0 23-13 34-24 34S76 117 76 94Z"/>
    <path d="M76 67H58v14q0 25 24 25m42-39h18v14q0 25-24 25m-18 24v17m-23 7h46" fill="none" stroke="currentColor" strokeWidth="8" strokeLinecap="round"/>
  </>;
}

function MedalSymbol({ definition, milestone, cutout }: { definition: AwardDefinition; milestone?: string; cutout: string }) {
  switch (definition.icon) {
    case "welcome": return <text {...lettering} x="100" y="112" fontSize="32">Welcome</text>;
    case "practice": return <text {...lettering} x="100" y="125" fontSize="70">1st</text>;
    case "book": return <g transform="translate(0 -12)">
      <path fillRule="evenodd" d="M43 75h7v65q27-9 50 8 23-17 50-8V75h7v76q-32-11-57 7-25-18-57-7Z"/>
      <path d="M54 65q26-9 43 9v63q-20-15-43-7Zm49 9q18-18 43-9v65q-24-8-43 7Z"/>
      {[80, 87, 94, 101, 108].map(y => <path key={y} d={"M62 " + y + "h26m24 0h26"} fill="none" stroke={cutout} strokeWidth="2.4" strokeLinecap="round"/>)}
    </g>;
    case "resource": return <>
      <path d="m53 82 65-28 16 34-66 23Z" strokeLinejoin="round"/>
      <path d="m114 50 15-6 20 43-15 7Z"/>
      <ellipse cx="139" cy="65.5" rx="4" ry="19" transform="rotate(-25 139 65.5)" fill={cutout} stroke="none"/>
      <path d="m48 85 8-4 12 26-8 4Z"/>
      <path d="m99 99 0 44m0-28-23 34m23-34 23 34" fill="none" stroke="currentColor" strokeWidth="8" strokeLinecap="round"/>
      <circle cx="99" cy="108" r="7"/>
    </>;
    case "perfect-week": return <>
      <text {...lettering} x="100" y="82" fontSize="25">Perfect</text>
      <text {...lettering} x="100" y="143" fontSize="72">7</text>
    </>;
    case "calendar": return <CalendarSymbol/>;
    case "checked-calendar": return <CalendarSymbol checked/>;
    case "collection": return <text {...lettering} x="100" y="129" fontSize={milestone && milestone.length > 2 ? 67 : 78}>{milestone}</text>;
    case "weekly-hour": return <text {...lettering} x="100" y="112" fontSize="44" letterSpacing="-1">60:00</text>;
    case "clock": return <ClockFace minutes={Number(milestone ?? 15)}/>;
    case "hourglass": return <>
      <path d="M73 49c0 33 7 41 27 52-20 11-27 19-27 50h54c0-31-7-39-27-50 20-11 27-19 27-52Z" fill="none" stroke="currentColor" strokeWidth="7" strokeLinejoin="round"/>
      <path d="M65 43h70M65 157h70" fill="none" stroke="currentColor" strokeWidth="8" strokeLinecap="round"/>
      <path d="M86 63h28q-2 15-14 24-12-9-14-24Z"/>
      <text {...lettering} x="100" y="141" fontSize="29">{milestone}</text>
    </>;
    case "return": return <>
      <path d="M60 43 103 63v82l-43 13Z" fill={cutout} stroke="none"/>
      <circle cx="93" cy="103" r="2.6"/>
      <g fill="#15161c" stroke="#15161c">
        <circle cx="116" cy="91" r="7.5"/>
        <path d="m113 105-3 22m3-20-8 10 2 7m6-17 9 10 3 6m-15 4 16 15 12 15m-28-30 5 18-5 17" fill="none" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round"/>
      </g>
    </>;
    case "target": return <>
      <circle cx="100" cy="84" r="38" fill="none" stroke="currentColor" strokeWidth="9"/>
      <path d="m78 85 15 15 30-32" fill="none" stroke="currentColor" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round"/>
      <text {...lettering} x="100" y="166" fontSize="40">{milestone}</text>
    </>;
    case "feedback": return <g transform="translate(9 -1)">
      <path d="M95 127c-1 15 19 22 27 8l8-17c6-11 15-21 15-38 0-21-14-35-33-35S79 59 79 80" fill="none" stroke="currentColor" strokeWidth="8" strokeLinecap="round"/>
      <path d="M97 83c0-18 27-18 27 1 0 12-17 13-17 26v6M63 68q-14 20 0 40M49 57q-24 31 0 62" fill="none" stroke="currentColor" strokeWidth="7" strokeLinecap="round"/>
    </g>;
    // The approved Repertoire Star is the star-shaped medal itself.
    case "shape-only": return null;
    case "check": return <g transform="translate(0 -12)">
      <rect x="59" y="65" width="82" height="82" rx="9" fill="none" stroke="currentColor" strokeWidth="8"/>
      <path d="m77 106 16 16 31-33" fill="none" stroke="currentColor" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round"/>
    </g>;
    case "reflection": return <>
      <path fillRule="evenodd" d="M56 50h88v100H56Zm8 8v84h72V58Z"/>
      <path d="m103 133 7-22 39-39 13 13-39 39Z"/>
      <path d="M81 77h37M81 92h28M81 107h17" fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round"/>
    </>;
    case "metronome": return <g transform="translate(0 -8)">
      <path fillRule="evenodd" d="M83 51h34l25 102H58Zm8 10-16 69h50l-16-69Z"/>
      <path d="M100 66v62m0-4 43-57" fill="none" stroke="currentColor" strokeWidth="7" strokeLinecap="round"/>
      <path d="m134 69 10-14 8 6-10 14Z"/>
    </g>;
    case "recording": return <g transform="translate(0 -7)">
      <rect x="83" y="54" width="34" height="67" rx="17" fill="none" stroke="currentColor" strokeWidth="6"/>
      <text {...lettering} x="100" y="99" fontSize="28">1</text>
      <path d="M70 103v7a30 30 0 0 0 60 0v-7m-30 37v17m-20 0h40" fill="none" stroke="currentColor" strokeWidth="8" strokeLinecap="round"/>
    </g>;
    case "creative": return <g transform="translate(26 36) scale(.74)">
      <path d="M82 125v-9c0-9-17-16-17-35a35 35 0 1 1 70 0c0 19-17 26-17 35v9Z" fill="none" stroke="currentColor" strokeWidth="8" strokeLinejoin="round"/>
      <path d="M90 124v-21l-10-10m30 31v-21l10-10m-36 43h32m-27 13h22M100 34V23M59 48l-9-9m91 9 9-9M48 88H35m117 0h13" fill="none" stroke="currentColor" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round"/>
    </g>;
    case "month": {
      const monthName = definition.month ? PERFECT_MONTH_NAMES[definition.month - 1]! : "Month";
      return <>
        <text {...lettering} x="100" y="84" fontSize="24">Perfect</text>
        <text {...lettering} x="100" y="121" fontSize={monthName.length > 8 ? 23 : 26}>{monthName}</text>
      </>;
    }
    case "legend": return milestone ? <>
      <g transform="translate(32 -2) scale(.68)"><Trophy/></g>
      <text {...lettering} x="100" y="146" fontSize={milestone.length > 2 ? 43 : 47}>{milestone}</text>
    </> : <g transform="translate(0 -10)"><Trophy/></g>;
    case "year": return <>
      <text {...lettering} x="100" y="77" fontSize="26">The</text>
      <text {...lettering} x="100" y="136" fontSize="61">365</text>
    </>;
    case "instrument": {
      if (!definition.lessonInstrument || !definition.lessonMilestone) return null;
      const label = lessonMilestoneLabel(definition.lessonMilestone);
      return <>
        <g transform="translate(0 -7)"><LessonInstrumentSymbol instrument={definition.lessonInstrument} cutout={cutout}/></g>
        <text {...lettering} x="100" y="156" fontSize={label.length > 4 ? 32 : 38}>{label}</text>
        <text {...lettering} x="100" y="168" fontSize="9" letterSpacing="1">{definition.lessonMilestone === 1 ? "LESSON" : "LESSONS"}</text>
      </>;
    }
  }
}

export function AwardMedal({ definition, earned, milestone }: {
  definition: AwardDefinition; earned: boolean; milestone?: string;
}) {
  const id = "medal-" + useId().replace(/[^a-zA-Z0-9_-]/g, "");
  if (definition.icon === "bug") return <img src="/awards/bug-hunter.png" alt="" aria-hidden="true"
    className={"award-medal award-medal-bug" + (earned ? "" : " is-locked")} />;
  const path = silhouettes[definition.shape];
  const colour = earned ? definition.colour : "var(--award-locked-face, #53555b)";
  const metals = { white: "#fff", chrome: "#9d9fa9", gold: "#e6c365", silver: "#d4d2ce" };
  const symbolEdge = earned ? metals[definition.metal] : "var(--award-locked-ink, #d4d2ce)";
  const number = milestone ?? definition.milestone?.toString();
  return <svg viewBox="0 0 200 212" className={"award-medal" + (earned ? "" : " is-locked")} aria-hidden="true" focusable="false">
    {definition.icon === "return" && <defs>
      <clipPath id={id + "-door-clip"}><path d={path} transform="translate(10 10) scale(.9)"/></clipPath>
    </defs>}
    <path d={path} fill={symbolEdge}/>
    <path d={path} transform="translate(10 10) scale(.9)" fill={colour}/>
    <path d={path} transform="translate(5 5) scale(.95)" fill="none" stroke={colour} strokeWidth="1"/>
    {definition.icon === "return" && <g clipPath={"url(#" + id + "-door-clip)"}>
      <path d="M60 43h68v82l37 32-54 13-8-25V63Z" fill="#fffdf2"/>
    </g>}
    <g fill={symbolEdge} stroke={symbolEdge} strokeWidth=".5" strokeLinejoin="round" color={symbolEdge}>
      <MedalSymbol definition={definition} milestone={number} cutout={colour}/>
    </g>
  </svg>;
}
